import React, { useCallback, useEffect, useState } from 'react';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useInView } from 'react-intersection-observer';
import Arrow, { ArrowDirection } from './Arrow';
import * as Styled from './CollectionCarousel.styles';
import { CollectionItem } from './CollectionItem';
import { CollectionCarouselProps } from './types';
import {
  CarouselAction,
  sendCarouselAnalyticsEvent,
  sendSeeAllAnalyticsEvent,
} from '../../helpers';
import useInViewAnalytics from '../../hooks/useInViewAnalytics';
import { useSeeAllData } from '../../hooks/useSeeAllData';

export const LIMIT_SLIDES = 12;

export const CollectionCarousel: React.FC<CollectionCarouselProps> = (
  props,
) => {
  const {
    title,
    isFeatured,
    campaigns,
    onSeeAllClick,
    isLoading = true,
    actionLabel = 'See all',
    eventLocation,
    ctaPosition = 'right',
    slideLimit = LIMIT_SLIDES,
  } = props;
  const [isInView, setIsInView] = useState(false);
  const [slidesInView, setSlidesInView] = useState<number[]>([]);
  const { ref: inViewRef } = useInView({
    threshold: 0.5,
    onChange: setIsInView,
  });
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
    slidesToScroll: 'auto',
  });
  const { contentId, contentType } = useSeeAllData(title);
  const { handleInViewChange } = useInViewAnalytics({
    campaigns,
    title,
    isInView,
  });

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setPrevBtnDisabled(!api.canScrollPrev());
    setNextBtnDisabled(!api.canScrollNext());
  }, []);

  const updateSlidesInView = useCallback((api: EmblaCarouselType) => {
    setSlidesInView((slidesInView) => {
      if (slidesInView.length === api.slideNodes().length) {
        api.off('slidesInView', updateSlidesInView);
      }
      const inView = api
        .slidesInView()
        .filter((index: number) => !slidesInView.includes(index));
      return slidesInView.concat(inView);
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    updateSlidesInView(emblaApi);
    emblaApi.on('slidesInView', updateSlidesInView);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect, updateSlidesInView]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      sendCarouselAnalyticsEvent(title, CarouselAction.BACK, eventLocation);
      emblaApi.scrollPrev();
    }
  }, [emblaApi, title, eventLocation]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      sendCarouselAnalyticsEvent(title, CarouselAction.FORWARD, eventLocation);
      emblaApi.scrollNext();
    }
  }, [emblaApi, title, eventLocation]);

  const handleSeeAllClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      sendSeeAllAnalyticsEvent(title, eventLocation);
      onSeeAllClick();
    },
    [onSeeAllClick, title, eventLocation],
  );

  const renderPlaceholderItems = () => {
    return Array.from({ length: 4 }).map((_, index) => (
      <CollectionItem
        key={`placeholder-${index}`}
        campaign={null}
        isFeatured={isFeatured}
        collectionTitle=""
        index={index}
        inView={false}
        eventLocation={eventLocation}
      />
    ));
  };

  if (campaigns.length === 0) return null;

  return (
    <Styled.Block isHomePage={false} ref={inViewRef} ctaPosition={ctaPosition}>
      <Styled.Header isHomePage={false} ctaPosition={ctaPosition}>
        <Styled.Title
          variant="h3"
          fontWeight="bold"
          onClick={handleSeeAllClick}
        >
          {title}
        </Styled.Title>
        <Styled.LinkWrapper>
          <Styled.Link
            color="primary"
            onClick={handleSeeAllClick}
            id={contentId}
            title={contentType}
          >
            {actionLabel}
          </Styled.Link>
        </Styled.LinkWrapper>
      </Styled.Header>
      <Styled.Wrapper>
        {!isLoading && (
          <>
            <Arrow
              direction={ArrowDirection.LEFT}
              onClick={scrollPrev}
              show={!prevBtnDisabled}
            />
            <Arrow
              direction={ArrowDirection.RIGHT}
              onClick={scrollNext}
              show={!nextBtnDisabled}
            />
          </>
        )}
        <Styled.Viewport ref={isLoading ? undefined : emblaRef}>
          <Styled.Content>
            {isLoading
              ? renderPlaceholderItems()
              : campaigns
                  .slice(0, slideLimit)
                  .map((campaign, index) => (
                    <CollectionItem
                      key={campaign._id}
                      campaign={campaign}
                      isFeatured={isFeatured}
                      sendInViewChange={handleInViewChange}
                      collectionTitle={title}
                      index={index}
                      inView={slidesInView.indexOf(index) > -1}
                      eventLocation={eventLocation}
                    />
                  ))}
          </Styled.Content>
        </Styled.Viewport>
      </Styled.Wrapper>
    </Styled.Block>
  );
};
